import { useLoaderData, useNavigate } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Alert } from '@ds/feedback';
import { Tabs } from '@ds/navigation';
import { PageHeader } from '@/comuns/PageHeader';
import { dados } from '@/data/porta';
import type { Agente, Fluxo, MeuOrcamentoDeIa } from '@/data/tipos';
import { rotaPorId } from '@/router/rotas';
import { AgentesDeAtendimento } from './agentes/AgentesDeAtendimento';
import { ConstrutorDeFluxos } from './agentes/ConstrutorDeFluxos';

export type AbaDeAgentes = 'atendimento' | 'fluxos';

const ABAS: AbaDeAgentes[] = ['atendimento', 'fluxos'];

export interface CargaDeAgentes {
    aba: AbaDeAgentes;
    agentes: Agente[];
    fluxos: Fluxo[];
    /** Sem chave na plataforma, ou com o teto estourado, nada disto responde — e a tela diz antes. */
    iaDisponivel: boolean;
    orcamento: MeuOrcamentoDeIa | null;
}

function abaDaRota(pathname: string): AbaDeAgentes {
    const ultimo = pathname.split('/').pop() ?? '';
    return (ABAS as string[]).includes(ultimo) ? (ultimo as AbaDeAgentes) : 'atendimento';
}

export async function carregarAgentes({ request }: LoaderFunctionArgs): Promise<CargaDeAgentes> {
    const [agentes, fluxos, iaDisponivel, orcamento] = await Promise.all([
        dados().agentes(),
        dados().fluxos(),
        dados().iaDisponivel(),
        dados().meuOrcamentoDeIa(),
    ]);
    return { aba: abaDaRota(new URL(request.url).pathname), agentes, fluxos, iaDisponivel, orcamento };
}

/**
 * Agentes e IA.
 *
 * Duas abas para duas coisas diferentes: os agentes de atendimento falam com o
 * PACIENTE e por isso precisam de ferramentas; os fluxos amarram gatilho,
 * condição e ação. O copiloto, que falava com a equipe sobre os números da
 * tela, saiu do produto em 13/09/2026.
 */
export function AgentesEIa() {
    const carga = useLoaderData() as CargaDeAgentes;
    const navigate = useNavigate();
    const rota = rotaPorId('agentes');

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Agentes e IA"
                description="Como a IA da clínica fala com o paciente, o que ela pode fazer e quando ela age sozinha."
            >
                <Tabs
                    ariaLabel="Seções de agentes e IA" activeId={carga.aba}
                    onChange={(id) => navigate(`/app/agentes/${id}`)}
                    tabs={(rota?.children ?? []).map((c) => ({ id: c.path, label: c.label }))}
                />
            </PageHeader>

            {!carga.iaDisponivel && (
                /* **São dois motivos diferentes, e a diferença importa.** Sem
                   chave, não há o que a clínica faça — é decisão da plataforma e
                   ela espera. Com o teto estourado, ela sabe quanto gastou, sabe
                   que volta na virada do mês e pode pedir aumento. Um aviso só,
                   genérico, mandaria abrir chamado nos dois casos. */
                carga.orcamento?.estourado
                    ? (
                        <Alert tone="warning" dismissible={false} title="A IA da clínica alcançou o teto do mês">
                            Esta clínica consumiu {carga.orcamento.tokensDoMes.toLocaleString('pt-BR')} de{' '}
                            {(carga.orcamento.limiteTokensMes ?? 0).toLocaleString('pt-BR')} tokens. Os agentes
                            continuam configuráveis; eles voltam a responder na virada do mês, ou antes se a
                            VecchIA aumentar o teto.
                        </Alert>
                    )
                    : (
                        /* A configuração pode ser feita sem chave — o que não dá é para
                           testá-la. Dizer isso antes evita a leitura de que a tela está
                           quebrada. */
                        <Alert tone="warning" dismissible={false} title="Nenhuma chave de IA está configurada na plataforma">
                            Você pode configurar os agentes agora; eles só passam a responder quando a Fórmula MED ligar uma
                            chave de provedor.
                        </Alert>
                    )
            )}

            {carga.aba === 'atendimento' && (
                <AgentesDeAtendimento agentes={carga.agentes} disponivel={carga.iaDisponivel} />
            )}

            {carga.aba === 'fluxos' && (
                <ConstrutorDeFluxos fluxos={carga.fluxos} disponivel={carga.iaDisponivel} />
            )}
        </div>
    );
}
